import { SyntheticEvent, useEffect, useMemo, useRef } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Autocomplete,
  AutocompleteChangeReason,
  Box,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Zone } from '@/__generated__/graphql';
import * as state from '@/components/AssetLabelling/state';
import * as globalState from '@/state';
import { useRouter } from '@/hooks';
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge';
import { abyssColors } from '@/theme/colors';
import { useZoneInitialCameraPose } from '../hooks/useZoneInitialCameraPose';
type Option = { zone: Zone | undefined | null };

const getOptionLabel = (option: Option) => {
  return option.zone?.name ?? '';
};

export const ZoneSelector = () => {
  const router = useRouter();
  const zoneIdFromUrl = router.query.zone;

  const [selectedZone, setSelectedZone] = useAtom(globalState.selectedZone);
  const setSelectedSpherical = useSetAtom(globalState.selectedSpherical);
  const zones = useAtomValue(globalState.zones);
  const currentCuboid = useAtomValue(state.currentCuboid);

  const hasSetFromUrlOnce = useRef(false);

  const { setZoneInitialCameraPose } = useZoneInitialCameraPose();

  useEffect(() => {
    if (hasSetFromUrlOnce.current) return;
    if (selectedZone) return;
    if (!zones) return;
    if (!zoneIdFromUrl) return;
    hasSetFromUrlOnce.current = true;
    const zone = zones.find((value) => value.id === zoneIdFromUrl);
    if (zone) {
      setSelectedZone(zone);
      setZoneInitialCameraPose(zone);
    }
  }, [zones, zoneIdFromUrl, selectedZone, setSelectedZone, setZoneInitialCameraPose]);

  useEffect(() => {
    if (!selectedZone) return;
    if (zoneIdFromUrl !== selectedZone.id) {
      router.replace({ query: { zone: selectedZone.id } });
    }
  }, [selectedZone, zoneIdFromUrl, router]);

  const selectedTag = useMemo(() => ({ zone: selectedZone }), [selectedZone]);
  const allTags = useMemo(() => zones?.map((zone) => ({ zone })) ?? [], [zones]);

  const onChange = async (
    _event: SyntheticEvent,
    value: Option | null,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption' && value?.zone) {
      setSelectedZone(value.zone);
      setZoneInitialCameraPose(value.zone);
      setSelectedSpherical(undefined);
    }
  };

  return (
    <>
      <Stack direction="row" width="100%">
        <Autocomplete
          fullWidth
          id="zone-selector"
          defaultValue={selectedTag}
          value={selectedTag}
          isOptionEqualToValue={(option, value) => value && option.zone?.id === value.zone?.id}
          options={allTags}
          getOptionLabel={getOptionLabel}
          disableClearable
          disableListWrap
          disabled={!!currentCuboid}
          autoHighlight
          onChange={onChange}
          renderInput={(parameters) => (
            <TextField
              {...parameters}
              label="Zone"
              variant="standard"
              fullWidth
              placeholder="Select zone..."
            />
          )}
          renderOption={(props, option: Option) => {
            return (
              <Box
                component="li"
                sx={{ '& > span': { mr: 2, flexShrink: 0 } }}
                {...props}
                key={option.zone?.id ?? 'empty'}
              >
                {option.zone?.name}
              </Box>
            );
          }}
        />
      </Stack>

      {!selectedZone && (
        <Stack
          sx={{ height: 'calc(100vh - 200px)' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <PhotoSizeSelectLargeIcon
            sx={{
              color: abyssColors.primary['300'],
              width: '48px',
              height: '48px',
            }}
          />
          <Typography
            variant="body1"
            sx={{
              width: '100%',
              textAlign: 'center',
              color: abyssColors.primary['300'],
            }}
          >
            Please select a zone to continue
          </Typography>
        </Stack>
      )}
    </>
  );
};
